import { useEffect, useRef, useState } from "react";

/**
 * Lightweight searchable dropdown. Type to filter, click to pick.
 * Props: value, onChange(value), options [{value, label}], placeholder, className, testid.
 */
export default function SearchableSelect({ value, onChange, options = [], placeholder = "Select…", className = "", testid }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const selected = options.find((o) => o.value === value);
  const q = query.trim().toLowerCase();
  const filtered = q ? options.filter((o) => (o.label || "").toLowerCase().includes(q)) : options;

  const pick = (val) => { onChange(val); setOpen(false); setQuery(""); };

  return (
    <div className="relative" ref={ref} data-testid={testid}>
      <input
        type="text"
        value={open ? query : (selected ? selected.label : "")}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => { setQuery(""); setOpen(true); }}
        placeholder={placeholder}
        title={selected ? selected.label : ""}
        className={className}
      />
      {open && (
        <ul className="absolute z-40 mt-1 min-w-full w-max max-w-[420px] max-h-72 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg text-sm">
          {value ? (
            <li
              onMouseDown={(e) => { e.preventDefault(); pick(""); }}
              className="px-3 py-1.5 hover:bg-orange-50 cursor-pointer text-slate-500 italic border-b border-slate-100"
            >
              Clear selection
            </li>
          ) : null}
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-slate-400 italic">No matches</li>
          ) : (
            filtered.map((o) => (
              <li
                key={o.value}
                onMouseDown={(e) => { e.preventDefault(); pick(o.value); }}
                className={`px-3 py-1.5 hover:bg-orange-50 cursor-pointer whitespace-nowrap ${o.value === value ? "bg-orange-50 text-orange-700" : "text-slate-800"}`}
              >
                {o.label}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
